import { memo } from "react";
import moment from 'moment';
import { Card } from 'react-bootstrap';

import {
    NullableNumber,
    ReportChartSeries,
    ReportItemType,
    ReportTimeRangeType,
} from "types/Reports";
import { REPORT_SENSOR_LIST, REPORT_SENSOR_REGISTRY } from 'constant/report';
import { DATE_TIME_FORMATS } from "constant";

import {
    getAverageAndPercentForTabItem,
    getTotalAndPercentForTabItem,
} from "./utils/report";
import { formatValuesInReport } from './utils/reportFormat';
import {
    averageByWithNull,
    getLowestHighestFromArray,
} from './utils/reportArray';

import ReportAverageText from './ReportAverageText';
import ReportBarChart from './ReportBarChart';
import ReportBulletText from './ReportBulletText';
import ReportPercentText from './ReportPercentText';
import ReportTabTotalText from './ReportTabTotalText';
import ReportItemEdgeText from './ReportItemEdgeText';
import ReportEmptyChart from "./ReportEmptyChart";
import ReportLoadingChart from './ReportLoadingChart';
import { getIndividualCardPrefix } from "./utils/reportString";

type ReportTabListItemProps = {
    curSeries: ReportChartSeries,
    prevSeries?: ReportChartSeries,

    isLoading: boolean;

    timeRangeType: ReportTimeRangeType;
    itemType: ReportItemType;

    categories: string[];
    time: moment.Moment;

    dateFormat: string;
    titleTimeText: string;

    highlightClassName: string;
    color: string;
};

export default memo(function ReportTabListItem({
    curSeries,
    prevSeries,

    isLoading,

    timeRangeType,
    itemType,

    categories,
    time,

    dateFormat,
    titleTimeText,

    highlightClassName,
    color,
}: Readonly<ReportTabListItemProps>) {
    let dataName = curSeries.name;
    let body = isLoading ? <ReportLoadingChart /> : <ReportEmptyChart />;
    let data: NullableNumber[] = [];
    let prevData: NullableNumber[] = [];
    let totalOrAvg: NullableNumber;
    let percent: NullableNumber;

    if ((REPORT_SENSOR_LIST as readonly ReportItemType[]).includes(itemType)) {
        const result = getAverageAndPercentForTabItem(curSeries, prevSeries, timeRangeType);
        data = result.data;
        prevData = result.prevData;
        percent = result.percent;
        totalOrAvg = result.avg;
    } else {
        const result = getTotalAndPercentForTabItem(curSeries, prevSeries, timeRangeType);
        data = result.data;
        prevData = result.prevData;
        percent = result.percent;
        totalOrAvg = result.total;
    }

    const isValid = data.some((item => item !== null));

    if (isValid && !isLoading) {
        const average = averageByWithNull(data, (v) => v);
        const prevAverage = prevData.length > 0 ? averageByWithNull(prevData, (v) => v) : null;
        const selectedMonth = time.format(DATE_TIME_FORMATS.MONTH_ONLY);
        const lastDayOfMonth = moment.unix(time.unix()).endOf('month');

        // format the data then find the highest items and lowest items to display
        const displayData = formatValuesInReport(data);

        const { highestItem, lowestItem } = getLowestHighestFromArray(displayData, categories, lastDayOfMonth);

        body = <>
            <ReportBulletText
                itemType={itemType}
                highlightClassName={highlightClassName}
                isOverview={false}
            />

            <ReportBarChart
                itemType={itemType}
                x={categories}
                y={data}
                unit={REPORT_SENSOR_REGISTRY[itemType].unit}
                color={color}
                yName={dataName}
            />

            <ReportPercentText
                percent={percent}
                itemType={itemType}
            />

            <ReportItemEdgeText
                isHighest={true}
                dateFormat={dateFormat}
                item={highestItem}
                itemType={itemType}
            />

            {
                itemType !== 'waterLeakage' &&
                <ReportItemEdgeText
                    isHighest={false}
                    dateFormat={dateFormat}
                    item={lowestItem}
                    itemType={itemType}
                />
            }

            {
                ['room', 'energy', 'water'].includes(itemType) &&
                <ReportAverageText
                    itemType={itemType}
                    average={average}
                    prevAverage={prevAverage}
                    unit={REPORT_SENSOR_REGISTRY[itemType].unit}
                    selectedMonth={selectedMonth}
                />
            }
        </>;
    }

    const prefix = getIndividualCardPrefix(itemType);

    return (
        <Card className="ouc">
            <h6 className="ouc-title">{prefix}{curSeries.name}{titleTimeText}</h6>

            <ReportTabTotalText
                itemType={itemType}
                isValid={isValid}
                total={totalOrAvg}
                textHighlightClassName={highlightClassName}
                unit={REPORT_SENSOR_REGISTRY[itemType].unit}
            />

            {body}
        </Card>
    );
});
